import { useCallback, useMemo, useState } from "react";

import { pollAverage } from "./model";
import { POLLS_AS_OF, RACE_POLLS } from "./polls";
import { SILVER_SOURCE } from "./silver";
import {
  loadActiveSource,
  loadSources,
  saveActiveSource,
  saveSources,
} from "./storage";
import type { ExternalSource, Layer } from "./types";

/* ── Types ─────────────────────────────────────── */

export type ExternalSources = ReturnType<typeof useExternalSources>;

/* ── Constants ─────────────────────────────────── */

/** The public polls' race averages, margins only. */
const POLLS_SOURCE: ExternalSource = {
  builtIn: true,
  id: "builtin-public-polls",
  importedAt: `${POLLS_AS_OF}T12:00:00`,
  label: "Public polls",
  rows: RACE_POLLS.flatMap(({ office, polls, state }) => {
    const average = pollAverage(polls);
    return average
      ? [{ leftProbability: null, margin: average.margin, office, state }]
      : [];
  }),
};

/* ── Hook ──────────────────────────────────────── */

/**
 * Published forecasts to compare Foresight with (admin view): Silver
 * Bulletin and the public polls built in plus the sources pasted in this
 * browser, the one compared, and the map layer read from it.
 */
const useExternalSources = () => {
  const [activeSourceId, setActiveSourceId] = useState<string | null>(() =>
    loadActiveSource(SILVER_SOURCE.id)
  );
  const [layer, setLayer] = useState<Layer>("foresight");
  const [sources, setSources] = useState<ExternalSource[]>(loadSources);

  const allSources = useMemo(
    () => [SILVER_SOURCE, POLLS_SOURCE, ...sources],
    [sources]
  );
  const activeSource = allSources.find((s) => s.id === activeSourceId) ?? null;
  const activeLayer: Layer = activeSource ? layer : "foresight";

  const activateSource = useCallback((id: string | null) => {
    setActiveSourceId(id);
    saveActiveSource(id);
    if (!id) setLayer("foresight");
  }, []);

  // Saving under an existing name merges into that source (newer rows win),
  // so a forecaster's Senate and governor tables can be pasted separately.
  const saveSource = useCallback(
    (source: ExternalSource) => {
      const key = source.label.trim().toLowerCase();
      const existing = sources.find(
        (s) => s.label.trim().toLowerCase() === key
      );
      const merged: ExternalSource = existing
        ? {
            ...existing,
            importedAt: source.importedAt,
            rows: [
              ...existing.rows.filter(
                (row) =>
                  !source.rows.some(
                    (r) =>
                      r.office === row.office &&
                      r.state === row.state &&
                      r.district === row.district
                  )
              ),
              ...source.rows,
            ],
          }
        : source;
      const next = existing
        ? sources.map((s) => (s.id === existing.id ? merged : s))
        : [...sources, merged];
      setSources(next);
      saveSources(next);
      activateSource(merged.id);
    },
    [activateSource, sources]
  );

  const deleteSource = useCallback(
    (id: string) => {
      setSources((list) => {
        const next = list.filter((s) => s.id !== id);
        saveSources(next);
        return next;
      });
      if (id === activeSourceId) activateSource(null);
    },
    [activateSource, activeSourceId]
  );

  const layerOptions: { label: string; value: Layer }[] = [
    { label: "Foresight", value: "foresight" },
    ...(activeSource
      ? [
          { label: activeSource.label, value: "external" as const },
          { label: "Ensemble", value: "ensemble" as const },
          { label: "Gap", value: "difference" as const },
        ]
      : []),
  ];

  return {
    activateSource,
    activeLayer,
    activeSource,
    activeSourceId,
    allSources,
    deleteSource,
    layerOptions,
    saveSource,
    setLayer,
  };
};

export default useExternalSources;
